import { useMemo, useRef } from 'react';
import { Text } from '@lmn/design-system';
import { parse, format } from 'date-fns';
import { useTranslation } from '@/app/components/translations/Translation';
import {
  getAvailableCancellationMealPlans,
  getRoomCancellationOptions,
} from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { createPriceByDisplayType, formatPrice } from '@/app/components/search/utils/formatting';
import { useIsMobile } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { getOptionKey } from '@/app/components/hotel-detail/rooms/roomCommon';
import { getPromotionDisplayLabel } from '@/app/components/hotel-detail/rooms/promotionLabels';
import {
  getCancellationBeforeDate,
  getCancellationDateformat,
  SHOPPING_API_DATE_FORMAT,
} from '@/app/components/hotel-detail/common/dates';
import { getPromotionGroupMealPlans } from './promotionGroup';
import { Option, OptionList, OptionRadio, PriceDifference, PromotionLabel } from './EvoRoomCancellationOptions.styled';

type EvoRoomCancellationOptionsProps = {
  room: RoomType;
  selectedMealPlan: MealPlanType;
  onSelectMealPlan?: (selectedMealPlan: MealPlanType) => void;
  // The committed selection the option set is derived from. When the radio group edits a draft (the
  // cancellation modal), pass the committed plan here so the rows don't re-derive and re-sort on every
  // click — re-selecting an option then always returns to the same rate. Defaults to `selectedMealPlan`.
  anchorMealPlan?: MealPlanType;
};

// Cancellation-type radio group shown inside EvoCancellationTypeSelectionModal: one row per
// available cancellation type (non-refundable / flexible up to a date), with the price difference
// between the options.
export function EvoRoomCancellationOptions(props: EvoRoomCancellationOptionsProps) {
  const { room, selectedMealPlan, onSelectMealPlan, anchorMealPlan = selectedMealPlan } = props;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const dynamicPriceChange = isMobile;
  const { localization } = usePageWrapperData();
  const { priceDisplayType } = localization.configValuesApp;
  const { showTotalPrice, familyTotalPrice } = usePriceDisplay();
  const { accommodationOnly } = useUrlSearchParams();

  // Only this card's (board × promotion group) rates take part in the option set (MPH-652). Deriving
  // from the full room and filtering afterwards is not equivalent: getAvailableCancellationMealPlans
  // keeps one representative per cancellation type and its score prefers a same-bed rate of another
  // group, which the group filter would then reject — hiding an option this group offers via another bed.
  const mealPlans = useMemo(
    () => getPromotionGroupMealPlans(room.configurations, anchorMealPlan),
    [room.configurations, anchorMealPlan]
  );
  const cancellationPolicies = useMemo(() => getRoomCancellationOptions(mealPlans), [mealPlans]);

  // The cancellation options this promotion group offers, each paired with the meal plan that backs it.
  const availableCancellationMealPlans = useMemo(
    () => getAvailableCancellationMealPlans(mealPlans, anchorMealPlan),
    [mealPlans, anchorMealPlan]
  );

  // The meal plan that backs a cancellation option. Candidates are already scoped to the anchor's
  // (room × promotionKey) card, so a cancellation type only backed by a rate of another card never
  // surfaces here — selecting it would silently swap the benefit shown on the card.
  const findOptionMealPlan = (type: CancellationType, upTo: string | undefined) =>
    availableCancellationMealPlans.find(
      (mealPlan) => mealPlan.cancellationPolicy.type === type && mealPlan.cancellationPolicy.upTo === upTo
    );

  // Compare in the displayed unit (rounded per market): server group total in total mode, else per person.
  const displayPrice = (mealPlan: MealPlanType) =>
    createPriceByDisplayType(showTotalPrice ? mealPlan.finalTotalPrice : mealPlan.finalPrice, priceDisplayType);
  // A family delta (MPH-1292) and a hotel-only delta carry no unit: a hotel-only price is a TOTAL price, so its
  // difference is already a whole-stay figure and must not be labelled per person either (MPH-1363). For a
  // package, the price toggler's total mode still labels its deltas "total" on purpose, and per-person mode
  // labels them per person.
  const priceUnitLabel =
    familyTotalPrice || accommodationOnly
      ? null
      : t(showTotalPrice ? 'vacation.oss.cards.total' : 'vacation.select.room.price.dp.perperson.short');

  // Each option paired with its backing meal plan — the single source for the sort order, the displayed
  // price difference and the rate applied on selection, so the three can never contradict each other.
  // Options without a backing plan (cancellation types the selected board class doesn't offer) are hidden;
  // they would render as disabled.
  // Order by price (ascending), then by free-cancellation date (ascending).
  // `upTo` is a `yyyy-MM-dd` string, so a lexicographic compare is chronological; options
  // without a date (non-refundable) sort first.
  const cancellationOptions = Object.values(cancellationPolicies)
    .flatMap(({ type, upTo }) => {
      const optionMealPlan = findOptionMealPlan(type, upTo);
      return optionMealPlan ? [{ type, upTo, optionMealPlan }] : [];
    })
    .sort((a, b) => {
      if (a.optionMealPlan.finalPrice !== b.optionMealPlan.finalPrice) {
        return a.optionMealPlan.finalPrice - b.optionMealPlan.finalPrice;
      }
      return (a.upTo ?? '').localeCompare(b.upTo ?? '');
    });

  // Non-flash promotions per rendered option, matched by index to `cancellationOptions`. A promotion
  // present on every option is already conveyed by the room card's promotions section, so only the
  // rate-specific ones (present on some options but not all) are surfaced below the option here.
  const optionPromotions = cancellationOptions.map(({ optionMealPlan }) =>
    (optionMealPlan.promotions ?? []).filter((promo) => promo.type !== 'FLASH_SALE')
  );
  const isPromotionInEveryOption = (type: string) =>
    optionPromotions.every((promos) => promos.some((p) => p.type === type));

  // Baseline for the static (desktop) view: the cheapest of the rendered options themselves — not the
  // cheapest rate of the whole board class, which can belong to another bed/promotion and would make the
  // labels contradict the row order.
  const cheapestOptionPrice = Math.min(
    ...cancellationOptions.map(({ optionMealPlan }) => displayPrice(optionMealPlan))
  );

  // ARIA radio-group keyboard pattern: the group is a single tab stop — only the checked option
  // (or the first one when none matches) is tabbable — and arrow keys move focus and selection
  // together, wrapping at the ends.
  const checkedIndex = cancellationOptions.findIndex(
    ({ type, upTo }) =>
      selectedMealPlan.cancellationPolicy.type === type && selectedMealPlan.cancellationPolicy.upTo === upTo
  );
  const tabStopIndex = checkedIndex === -1 ? 0 : checkedIndex;
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

  const moveSelection = (index: number, delta: number) => {
    const nextIndex = (index + delta + cancellationOptions.length) % cancellationOptions.length;
    onSelectMealPlan?.(cancellationOptions[nextIndex].optionMealPlan);
    optionRefs.current[nextIndex]?.focus();
  };

  return (
    // t() returns '' for a missing key, and an empty name must never render
    <OptionList role="radiogroup" aria-label={t('vacation.select.room_panel.cancellation') || undefined}>
      {cancellationOptions.map(({ type, upTo, optionMealPlan }, index) => {
        const checked = index === checkedIndex;

        // Promotions on this option's rate that aren't offered on every option (rate-specific).
        const rateSpecificPromotions = optionPromotions[index].filter((promo) => !isPromotionInEveryOption(promo.type));

        // Mobile (dynamicPriceChange): every non-selected option shows its price relative to the
        // currently selected option, so the selected row shows nothing.
        // Desktop: the static view comparing each option against the cheapest option shown.
        const priceDifferenceToUse = dynamicPriceChange
          ? checked
            ? 0
            : displayPrice(optionMealPlan) - displayPrice(selectedMealPlan)
          : displayPrice(optionMealPlan) - cheapestOptionPrice;

        const priceDifferenceText =
          priceDifferenceToUse === 0
            ? ''
            : `${priceDifferenceToUse > 0 ? '+' : '-'} ${formatPrice({ price: Math.abs(priceDifferenceToUse), localization })}`;

        return (
          <Option
            key={`${type}-${upTo}`}
            ref={(el: HTMLDivElement | null) => {
              optionRefs.current[index] = el;
            }}
            tabIndex={index === tabStopIndex ? 0 : -1}
            role="radio"
            aria-checked={checked}
            onClick={() => onSelectMealPlan?.(optionMealPlan)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                const currentTarget = e.currentTarget as HTMLElement;
                onSelectMealPlan?.(optionMealPlan);
                // Maintain focus after selection using requestAnimationFrame
                requestAnimationFrame(() => {
                  currentTarget.focus();
                });
              } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                e.stopPropagation();
                moveSelection(index, 1);
              } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                e.stopPropagation();
                moveSelection(index, -1);
              }
            }}
          >
            <OptionRadio
              label={<CancellationOptionLabel type={type} upTo={upTo} promotions={rateSpecificPromotions} />}
              name={`cancellation-${room.roomId}-${room.roomsIncludedInPrice}`}
              value={`${type}-${upTo}`}
              readOnly={true}
              data-testid={`room-option-${type}`}
              hoverBackground={false}
              checked={checked}
              tabIndex={-1}
            />

            {priceDifferenceText && (
              <PriceDifference>
                <Text variant="Title5" forceTag="span">
                  {priceDifferenceText}
                </Text>
                {priceUnitLabel && (
                  <Text variant="BodySmall" weight="bold">
                    {priceUnitLabel}
                  </Text>
                )}
              </PriceDifference>
            )}
          </Option>
        );
      })}
    </OptionList>
  );
}

function CancellationOptionLabel({
  type,
  upTo,
  promotions,
}: {
  type: CancellationType;
  upTo?: string;
  promotions: MealPlanPromotion[];
}) {
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  const { localization } = usePageWrapperData();

  const label = `${t(getOptionKey(type, accommodationOnly))}${
    type === 'REFUNDABLE' && upTo
      ? ` ${t('vacation.common.mealplan.room.cancellationtype.refundable.before', {
          date: format(
            parse(getCancellationBeforeDate(upTo), SHOPPING_API_DATE_FORMAT, new Date()),
            getCancellationDateformat(localization.locale)
          ),
        })}`
      : ''
  }`;

  const optionLabel = (
    <Text variant="Body" color="textPrimary">
      {label}
    </Text>
  );

  if (promotions.length === 0) return optionLabel;

  // Promotion labels stack below the option name, mirroring the non-EVO room panel meal-plan options.
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        row-gap: 4px;
      `}
    >
      {optionLabel}
      {promotions.map((promotion) => (
        <PromotionLabel key={promotion.label}>{getPromotionDisplayLabel(promotion, t)}</PromotionLabel>
      ))}
    </div>
  );
}
